/**
 * Shared parser for `ndc.NDC_Products.active_ingredients` — a raw OpenFDA
 * JSON array string, e.g. `[{"name":"ASENAPINE MALEATE","strength":"5 mg/1"}]`.
 * Extracted from NDCPanel.tsx (issue #585 Task 9.2) so the manufacturer
 * modal's linked-products detail view (MFGV3-006) can reuse the exact same
 * parsing instead of duplicating it.
 */

export interface NdcActiveIngredient {
  name: string;
  strength: string | null;
}

/**
 * Full parse — name + strength per ingredient. Returns [] for null/malformed
 * input rather than throwing; this is per-record source data, not something
 * to crash a modal over.
 */
export function parseNdcActiveIngredients(
  raw: string | null | undefined
): NdcActiveIngredient[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter(
        (entry): entry is { name?: unknown; strength?: unknown } =>
          entry !== null && typeof entry === 'object'
      )
      .map(entry => ({
        name: typeof entry.name === 'string' ? entry.name : '',
        strength: typeof entry.strength === 'string' ? entry.strength : null,
      }))
      .filter(entry => entry.name !== '');
  } catch {
    return [];
  }
}

/** Names only — NDCPanel's original shape, kept for that call site. */
export function parseNdcActiveIngredientNames(
  raw: string | null | undefined
): string[] {
  return parseNdcActiveIngredients(raw).map(entry => entry.name);
}
